import React, { useEffect, useRef, useState } from "react";
import "../confluent-dark.css";

const sources = [
  { icon: "🔥", label: "Firewalls", tooltip: "Next-gen firewall platforms", top: 80 },
  { icon: "🛡️", label: "EDR/XDR", tooltip: "Endpoint detection and response", top: 190 },
  { icon: "☁️", label: "Cloud", tooltip: "Cloud infrastructure logs", top: 300 },
  { icon: "📊", label: "SIEM Agents", tooltip: "System log collectors", top: 410 },
  { icon: "💻", label: "Applications", tooltip: "Web and database applications", top: 520 },
  { icon: "🌐", label: "Network", tooltip: "Network infrastructure devices", top: 630 },
  { icon: "🔐", label: "Identity", tooltip: "Identity and access management", top: 740 },
  { icon: "📧", label: "Email", tooltip: "Email security platforms", top: 850 },
];

const destinations = [
  { logo: "https://cdn.ivaluetv.com/oem/splunk.svg", label: "Splunk", tooltip: "Splunk Enterprise SIEM", top: 150, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/elastic.png", label: "Elastic", tooltip: "Elastic Security Analytics", top: 260, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/OpenText_logo.svg", label: "ArcSight", tooltip: "ArcSight Threat Detection", top: 370, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/oem/Chronicle-RGB-Horz-Color.webp", label: "Google Chronicle", tooltip: "Google Chronicle", top: 480, color: "#3b82f6" },
  { logo: "https://cdn.ivaluetv.com/public/icons8-data-lake-64.png", label: "Data Lake", tooltip: "Long-term compliance storage", top: 620, color: "#8b5cf6" },
];

const valueProps = [
  { icon: "⚡", title: "Real-time Streaming", desc: "Ingest, process, and route security data instantly." },
  { icon: "🔒", title: "Secure by Design", desc: "End-to-end encryption and access controls." },
  { icon: "🤖", title: "AI Enrichment", desc: "Automated threat intelligence and context." },
  { icon: "💾", title: "Cost-Optimized Storage", desc: "Tiered storage for compliance and forensics." },
  { icon: "🔌", title: "Multi-SIEM Integration", desc: "Connect to Splunk, Elastic, ArcSight, and more." },
  { icon: "📊", title: "Analytics Ready", desc: "Data is normalized and ready for analytics." },
];

const PATHS = [
  { key: 1, label: "🔴 Path 1: Direct (Legacy)", color: "#ef4444", class: "path1" },
  { key: 2, label: "🔵 Path 2: Filtered", color: "#3b82f6", class: "path2" },
  { key: 3, label: "🟢 Path 3: AI-Enriched", color: "#10b981", class: "path3" },
  { key: 4, label: "🟣 Path 4: Archive", color: "#8b5cf6", class: "path4" },
];


function ConfluentArchitecture() {
  // Only one path active at a time; default to Path 1
  const [activePath, setActivePath] = useState(1);
  // Track if a path is being hovered (for temporary preview)
  const [hoveredPath, setHoveredPath] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, text: "", x: 0, y: 0 });
  const diagramRef = useRef();
  const svgRef = useRef();

    // injectStyle function removed, styles are imported from CSS file

  // Particle background
  useEffect(() => {
    if (document.getElementById("particles")) return;
    const container = document.createElement("div");
    container.className = "particles";
    container.id = "particles";
    document.body.appendChild(container);
    for (let i = 0; i < 100; i++) {
      const particle = document.createElement("div");
      particle.className = "particle";
      particle.style.left = Math.random() * 100 + "%";
      particle.style.top = Math.random() * 100 + "%";
      particle.style.animationDelay = Math.random() * 3 + "s";
      container.appendChild(particle);
    }
    return () => {
      container.remove();
    };
  }, []);

  // Path toggle logic (single active)
  const handlePathClick = (key) => {
    setActivePath(key);
  };
  const handlePathHover = (key) => {
    setHoveredPath(key);
  };
  const handlePathLeave = () => {
    setHoveredPath(null);
  };

  // Tooltip handlers
  const showTooltip = (text, e) => {
    setTooltip({ show: true, text, x: e.pageX, y: e.pageY });
  };
  const hideTooltip = () => setTooltip({ show: false, text: "", x: 0, y: 0 });

  // SVG path drawing logic (only one path visible at a time)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const wrapper = diagramRef.current;
    if (!wrapper) return;
    svg.setAttribute("width", wrapper.offsetWidth);
    svg.setAttribute("height", wrapper.offsetHeight);

    // Source/dest positions
    const srcs = sources.map((s) => ({ x: 130, y: s.top + 40 }));
    const dsts = destinations.map((dst) => ({ x: wrapper.offsetWidth - 130, y: dst.top + 40 }));
    const center = { x: wrapper.offsetWidth / 2, y: wrapper.offsetHeight / 2 };

    // Helper for cubic curve
    function createPath(x1, y1, x2, y2, color, style = "solid", width = 2, opacity = 1) {
      const dx = x2 - x1;
      const dy = y2 - y1;
      const ctrl1x = x1 + dx * 0.3;
      const ctrl1y = y1 + dy * 0.1;
      const ctrl2x = x1 + dx * 0.7;
      const ctrl2y = y2 - dy * 0.1;
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      const d = `M ${x1} ${y1} C ${ctrl1x} ${ctrl1y}, ${ctrl2x} ${ctrl2y}, ${x2} ${y2}`;
      path.setAttribute("d", d);
      path.setAttribute("stroke", color);
      path.setAttribute("stroke-width", width);
      path.setAttribute("fill", "none");
      path.setAttribute("opacity", opacity);
      path.setAttribute("stroke-dasharray", style === "dashed" ? "10 5" : "0");
      path.style.filter = `drop-shadow(0 0 6px ${color})`;
      path.style.transition = "opacity 0.5s ease";
      svg.appendChild(path);
    }

    // Determine which path to show: hoveredPath (if set), else activePath
    const pathToShow = hoveredPath || activePath;

    if (pathToShow === 1) {
      // Path 1: Direct (red, dashed)
      srcs.forEach((source) => {
        dsts.forEach((dest) => {
          createPath(source.x, source.y, dest.x, dest.y, "#ef4444", "dashed", 2, 0.7);
        });
      });
    } else if (pathToShow === 2) {
      // Path 2: Filtered (blue)
      srcs.forEach((source) => {
        createPath(source.x, source.y, center.x - 100, center.y, "#3b82f6", "solid", 2, 0.8);
      });
      dsts.slice(0, dsts.length - 1).forEach((dest) => {
        createPath(center.x + 100, center.y, dest.x, dest.y, "#3b82f6", "solid", 2.5, 0.9);
      });
    } else if (pathToShow === 3) {
      // Path 3: AI-Enriched (green/purple with AI node)
      const aiPos = { x: center.x, y: center.y - 200 }; // AI position above Confluent
      
      // Step 1: Sources to Confluent Hub (blue)
      srcs.forEach((source) => {
        createPath(source.x, source.y, center.x - 100, center.y, "#3b82f6", "solid", 2.5, 0.85);
      });
      
      // Step 2: Confluent to AI (purple, bidirectional feel - vertical connection)
      createPath(center.x, center.y - 60, aiPos.x, aiPos.y + 60, "#a855f7", "solid", 3, 1);
      
      // Step 3: AI back to Confluent (purple - slightly offset for visual effect)
      createPath(aiPos.x, aiPos.y + 60, center.x, center.y - 60, "#a855f7", "solid", 3, 0.7);
      
      // Step 4: Confluent to SIEM platforms (green)
      dsts.slice(0, dsts.length - 1).forEach((dest) => {
        createPath(center.x + 100, center.y, dest.x, dest.y, "#10b981", "solid", 3.5, 1);
      });
    } else if (pathToShow === 4) {
      // Path 4: Archive (purple)
      srcs.forEach((source) => {
        createPath(source.x, source.y, center.x - 80, center.y - 60, "#8b5cf6", "solid", 1.8, 0.8);
      });
      if (dsts.length > 0) {
        createPath(center.x + 80, center.y - 60, dsts[dsts.length - 1].x, dsts[dsts.length - 1].y, "#8b5cf6", "solid", 2.5, 0.9);
      }
    }
  }, [activePath, hoveredPath]);

  // Responsive redraw
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        setActivePaths((p) => ({ ...p })); // force redraw
      }, 200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="confluent-arch-root" style={{ background: "#000", minHeight: "100vh", position: "relative", zIndex: 1, paddingTop: 80 }}>
      <div className="container" style={{ paddingTop: 20 }}>
        <div className="header">
          <h1>Confluent Center of Excellence</h1>
          <p>Modern Security Data Platform Architecture</p>
        </div>
        <div className="controls">
          {PATHS.map((p) => (
            <button
              key={p.key}
              className={`path-toggle ${p.class}${(hoveredPath ? hoveredPath === p.key : activePath === p.key) ? " active" : ""}`}
              data-path={p.key}
              onClick={() => handlePathClick(p.key)}
              onMouseEnter={() => handlePathHover(p.key)}
              onMouseLeave={handlePathLeave}
            >
              <span>{p.label}</span>
            </button>
          ))}
        </div>
        <div className="diagram-wrapper" ref={diagramRef} style={{ height: 1000, width: "100%" }}>
          <svg className="svg-canvas" ref={svgRef} />
          
          {/* AI Processing Node (Above Confluent) - Show only for Path 3 */}
          {((hoveredPath || activePath) === 3) && (
            <div style={{ 
              position: 'absolute', 
              left: '50%', 
              top: '50%', 
              transform: 'translate(-50%, -300px)', 
              zIndex: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              animation: 'aiPulse 3s ease-in-out infinite'
            }}>
              <div style={{ 
                border: '4px solid #a855f7', 
                borderRadius: '50%', 
                width: 90, 
                height: 90, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                fontSize: 42,
                background: 'radial-gradient(circle, rgba(168, 85, 247, 0.25) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(88, 28, 135, 0.3) 100%)',
                boxShadow: '0 0 30px rgba(168, 85, 247, 0.8), 0 0 60px rgba(168, 85, 247, 0.4), inset 0 0 20px rgba(168, 85, 247, 0.2)',
                position: 'relative',
                transition: 'all 0.3s ease',
              }}>
                <span style={{ filter: 'drop-shadow(0 0 8px rgba(168, 85, 247, 0.9))' }}>🤖</span>
                {/* Outer ring effect */}
                <div style={{
                  position: 'absolute',
                  width: '120%',
                  height: '120%',
                  border: '2px solid rgba(168, 85, 247, 0.3)',
                  borderRadius: '50%',
                  animation: 'aiRing 2s linear infinite'
                }} />
              </div>
              <div style={{ 
                color: '#a855f7', 
                fontWeight: 700, 
                fontSize: 17, 
                marginTop: 14, 
                textShadow: '0 0 15px rgba(168, 85, 247, 1), 0 0 30px rgba(168, 85, 247, 0.5)',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                AI Enrichment
              </div>
            </div>
          )}
          
          {/* Central Confluent Hub: hide for Path 1, show for others */}
          {((hoveredPath || activePath) !== 1) && (
            <div className="confluent-hub">
              <div className="hub-core">
                <img src="https://cdn.ivaluetv.com/oem/Confluent.png" alt="Confluent Logo" style={{ width: 'auto', height: 60, objectFit: 'contain', filter: 'invert(1) brightness(2)' }} />
                <div className="hub-text">Confluent Hub</div>
              </div>
            </div>
          )}
          {/* Log Sources (Left) */}
          {sources.map((src, i) => (
            <div
              className="icon-node"
              key={src.label}
              style={{ left: 50, top: src.top }}
              data-tooltip={src.tooltip}
              onMouseEnter={(e) => showTooltip(src.tooltip, e)}
              onMouseMove={(e) => showTooltip(src.tooltip, e)}
              onMouseLeave={hideTooltip}
            >
              <div className="icon-circle" style={{ borderColor: "#60a5fa", color: "#60a5fa" }}>
                <span className="icon-text">{src.icon}</span>
              </div>
              <div className="icon-label" style={{ marginTop: 12, color: '#e0e7ef', fontWeight: 500, fontSize: 12, textAlign: 'center', textShadow: '0 1px 6px #000' }}>{src.label}</div>
            </div>
          ))}
          {/* Destinations (Right) */}
          {destinations.map((dst, i) => (
            <div
              className="icon-node"
              key={dst.label}
              style={{ right: 50, top: dst.top }}
              data-tooltip={dst.tooltip}
              onMouseEnter={(e) => showTooltip(dst.tooltip, e)}
              onMouseMove={(e) => showTooltip(dst.tooltip, e)}
              onMouseLeave={hideTooltip}
            >
              <div className="icon-circle" style={{ borderColor: dst.color, color: dst.color, background: '#fff' }}>
                <img src={dst.logo} alt={dst.label + ' logo'} style={{ width: 40, height: 40, objectFit: 'contain', filter: 'drop-shadow(0 0 8px ' + dst.color + ')' }} />
              </div>
              <div className="icon-label">{dst.label}</div>
            </div>
          ))}
        </div>
        <div className="value-section">
          <h2 style={{ textAlign: "center", color: "#60a5fa", fontSize: "2em", marginBottom: 10 }}>Strategic Value Proposition</h2>
          <p style={{ textAlign: "center", color: "#94a3b8", marginBottom: 20 }}>Transform your security operations with Confluent</p>
          <div className="value-grid">
            {valueProps.map((v, i) => (
              <div className="value-item" key={v.title}>
                <div className="icon">{v.icon}</div>
                <h3>{v.title}</h3>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Tooltip */}
        <div
          className="tooltip"
          style={{
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            opacity: tooltip.show ? 1 : 0,
            pointerEvents: "none",
            position: "fixed",
            zIndex: 1000,
            transition: "opacity 0.3s ease",
          }}
        >
          {tooltip.text}
        </div>
      </div>
    </div>
  );
}

export default ConfluentArchitecture;
